/**
 * @file ProductFactsRowsPanel.tsx
 * @description Подпанель «Какие строки показывать» элемента «Сведения о
 * товаре» (phase19.16 Р2а, приёмка; SB35, решение пользователя 2026-10-11):
 * переключатель на каждую строку `PRODUCT_FACT_ROWS` в порядке вывода на
 * витрине. У строк с `legal` — пометка `PRODUCT_FACT_LEGAL_NOTE` без замка:
 * выключать можно («риски беру на себя»). Плашки «БАД» и «Не для
 * употребления» строками не управляются — они видны всегда при признаке.
 *
 * Реестр (OCP): новая строка добавляется в shared — здесь ничего не меняется.
 */

import { PRODUCT_FACT_LEGAL_NOTE, PRODUCT_FACT_ROWS, type ProductFactRowKey, type ProductFactRows } from "@chaiberi/shared";
import { useId } from "react";

import { Label } from "../../../../ui/label";
import { Switch } from "../../../../ui/switch";

interface Props {
  rows: ProductFactRows;
  onChange: (rows: ProductFactRows) => void;
}

export function ProductFactsRowsPanel({ rows, onChange }: Props) {
  const titleId = useId();
  // Меняем одну строку, остальные ключи не трогаем (strictObject на сервере).
  const toggle = (key: ProductFactRowKey, on: boolean) => onChange({ ...rows, [key]: on });

  return (
    <div role="group" aria-labelledby={titleId} className="flex flex-col">
      <Label id={titleId}>Какие строки показывать</Label>
      <ul className="mt-1 flex flex-col">
        {PRODUCT_FACT_ROWS.map((row) => (
          <FactRowSwitch key={row.key} label={row.label} legal={row.legal} checked={rows[row.key]} onChange={(on) => toggle(row.key, on)} />
        ))}
      </ul>
      <p className="type-caption mt-1 text-on-surface-variant">Плашки «БАД» и «Не для употребления» показываются всегда, если признак стоит у товара.</p>
    </div>
  );
}

interface RowProps {
  label: string;
  legal: boolean;
  checked: boolean;
  onChange: (on: boolean) => void;
}

/**
 * Строка-переключатель: подпись с переключателем нажимается целиком, высота
 * от 48 (касание на 360). Пометка закона — под подписью, вне `<label>`: base-ui
 * берёт имя переключателя из всей метки, пометка идёт описанием, а не именем.
 */
function FactRowSwitch({ label, legal, checked, onChange }: RowProps) {
  const noteId = useId();
  return (
    <li className="flex flex-col">
      <label className="flex min-h-12 cursor-pointer items-center gap-3">
        <Switch checked={checked} onCheckedChange={(on) => onChange(on)} aria-describedby={legal ? noteId : undefined} />
        <span className="type-body min-w-0 text-on-surface">{label}</span>
      </label>
      {legal ? (
        // pl-12 = ширина переключателя (w-9) + зазор (gap-3): пометка ровно под подписью.
        <span id={noteId} className="type-caption -mt-2 pb-1 pl-12 text-on-surface-variant">
          {PRODUCT_FACT_LEGAL_NOTE}
        </span>
      ) : null}
    </li>
  );
}
